"use client"

import { useQuery } from "@tanstack/react-query"
import { BarChart3, Share2 } from "lucide-react"

import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { getPopulationSummary } from "@/lernpfad-population-client"
import RidgelineChart from "../charts/ridgeline-chart"
import SankeyChart from "../charts/sankey-chart"

/**
 * The two population-shape charts, side by side under the impact band.
 *
 * They answer the questions the five headline figures cannot: the band says the
 * total moved, these say WHO it moved for and WHERE it went. Both re-render from
 * the same debounced query the band uses, so nothing extra is fetched.
 */
export default function ImpactCharts({ state }: { state: LernpfadDemoState }) {
    const { t, localizer, committedConstraints, baselineConstraints, policy } = state

    const baseline = useQuery({
        queryKey: ["lernpfad-summary", baselineConstraints],
        queryFn: () => getPopulationSummary(baselineConstraints),
        staleTime: Infinity,
    })
    const tuned = useQuery({
        queryKey: ["lernpfad-summary", committedConstraints],
        queryFn: () => getPopulationSummary(committedConstraints),
        enabled: policy !== undefined,
        placeholderData: (previous) => previous,
        staleTime: Infinity,
    })

    const current = policy ? tuned.data : baseline.data
    if (!baseline.data || !current) return null

    return (
        <div className="grid gap-4 lg:grid-cols-2">
            <section className="lp-card rounded-xl border bg-card">
                <header className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2.5">
                    <BarChart3 className="size-3.5 shrink-0 text-muted-foreground" />
                    <h3 className="text-sm font-medium">{t("steuer.chart.distribution")}</h3>
                </header>
                <div className="p-4">
                    <p className="mb-3 text-xs text-muted-foreground">
                        {t("steuer.chart.distributionHint", { count: localizer.num(baseline.data.headcount) })}
                    </p>
                    <RidgelineChart
                        baseline={baseline.data.hoursHistogram}
                        tuned={policy ? current.hoursHistogram : undefined}
                        labels={{
                            baseline: t("steuer.chart.standard"),
                            tuned: t("steuer.chart.tuned"),
                            axis: t("steuer.chart.hoursAxis"),
                            people: t("steuer.chart.people"),
                        }}
                    />
                </div>
            </section>

            <section className="lp-card rounded-xl border bg-card">
                <header className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2.5">
                    <Share2 className="size-3.5 shrink-0 text-muted-foreground" />
                    <h3 className="text-sm font-medium">{t("steuer.chart.flow")}</h3>
                </header>
                <div className="p-4">
                    <p className="mb-3 text-xs text-muted-foreground">{t("steuer.chart.flowHint")}</p>
                    <SankeyChart
                        flow={current.flow}
                        originLabel={(origin) =>
                            origin === "pflicht" || origin === "voraussetzung"
                                ? localizer.reason(origin)
                                : localizer.origin(origin as "regulatorik" | "rolle" | "mag" | "ziel")
                        }
                        modalityLabel={(modality) => t(`steuer.mod.${modality}` as never)}
                        hoursLabel={(hours) => `${localizer.num(hours)} ${t("steuer.impact.hours")}`}
                    />
                </div>
            </section>
        </div>
    )
}
